import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "theme"; // 'light' | 'dark' (absence => default dark)

function applyResolvedTheme(resolvedTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const isDark = resolvedTheme === "dark";
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
}

export function useThemeMode() {
  const [savedTheme, setSavedTheme] = useState(() => {
    try {
      const fromStorage = localStorage.getItem(STORAGE_KEY);
      return fromStorage ?? "dark";
    } catch {
      return "dark";
    }
  });

  const resolvedTheme = useMemo(() => {
    return savedTheme === "light" ? "light" : "dark";
  }, [savedTheme]);

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((nextTheme) => {
    const normalized = nextTheme === "dark" ? "dark" : "light";
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      // no-op
    }
    setSavedTheme(normalized);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme, setTheme]);

  return {
    theme: resolvedTheme,
    resolvedTheme,
    setTheme,
    toggleTheme,
  };
}
